import fetchBusinessBudget from 'actions/businessTraveller/fetchBusinessBudget'
import { getBedbankPropertyLocation } from 'api/accommodation'
import getBedbankPropertyIdFromSearchItem from 'businessTraveller/utils/getBedbankPropertyIdFromSearchItem'
import generateBusinessBudgetKey from 'businessTraveller/utils/generateBusinessBudgetKey'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useContext, useEffect, useMemo, useState } from 'react'
import useQueryParams from 'hooks/useQueryParams'
import { getDestinationIdFromURLSearchParams, getLandmarkIdFromURLSearchParams, getSearchItemFromURLSearchParams } from 'lib/url/searchUrlUtils'
import { getLocationDataFromOffer } from 'businessTraveller/utils/getLocationDataFromOffer'
import fetchBusinessAccommodationPolicy from 'actions/businessTraveller/fetchBusinessAccommodationPolicy'
import fetchBusinessApprovalPolicy from 'actions/businessTraveller/fetchBusinessApprovalPolicy'
import { usePlaceByCoords } from 'hooks/usePlace'
import { getCenterCoordinates } from 'lib/search/searchUtils'
import { getOfferIdFromSearchItem } from 'components/Search/utils'
import useOffer from 'hooks/Offers/useOffer'

interface UseBusinessTravellerBusinessBudgetFetcherOptions {
  offerRequired?: boolean
  placeId?: string
  lat?: number
  lng?: number
  enabled?: boolean
}

/**
 * Hook to fetch business budget data for LEBT for the current location.
 *
 * The budget endpoint requires either a placeId or longitude/latitude coordinates.
 *
 * This hook determines the location context from multiple sources:
 *
 * 1. **Direct Offer Parameter**: If an offer is passed directly to the hook, it extracts location data
 *    (latitude/longitude) from the offer's location information.
 *
 * 2. **URL Query Parameters**:
 *    - Extracts offer ID from search parameters and fetches the corresponding offer from Redux, and then does the above, or
 *    - Gets destination ID (destinationId) from URL search parameters, or
 *    - Gets landmark ID (landmarkId) from URL search parameters
 *
 * 3. **Map View Coordinates**: When in map view mode:
 *    - Parses bounds coordinates from search item
 *    - Calculates center coordinates from the map bounds
 *    - Uses these coordinates to fetch place information (lePlaceId)
 *
 * The hook constructs a "toBeCalculatedBusinessBudget" object containing:
 * - Currency code (from GeoContext)
 * - Location identifier (either lePlaceId or latitude/longitude coordinates)
 *
 * It then generates a unique budget key and fetches the corresponding business budget data,
 * accommodation policy, and approval policy for the current business account.
 *
 * @param offer - Optional offer object to extract location data from
 * @param options - Configuration options
 * @param options.offerRequired - If true, the hook will only fetch budget data when an offer is available
 * @param options.placeId - Optional placeId/destinationId to use instead of the one from the URL search params
 *
 * @returns Object containing:
 * - budgetData: The fetched business budget data
 * - budgetKey: The unique key used to identify the budget
 * - toBeCalculatedBusinessBudget: The budget calculation parameters
 */
function useBusinessTravellerBusinessBudgetFetcher(offer?: App.AnyOffer, options: UseBusinessTravellerBusinessBudgetFetcherOptions = {}) {
  const { offerRequired = false, placeId, lat, lng, enabled = true } = options
  const { currentCurrency: currencyCode } = useContext(GeoContext)
  const appDispatch = useAppDispatch()
  const businessBudgets = useAppSelector(state => state.businessTraveller.businessBudgets)
  const businessId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.business?.id)
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const queryParams = useQueryParams()
  const [propertyLocation, setPropertyLocation] = useState<{ latitude: number, longitude: number }>()

  // Search item from query params
  const searchItem = useMemo(() => getSearchItemFromURLSearchParams(queryParams), [queryParams])
  const bedbankPropertyId = useMemo(() => getBedbankPropertyIdFromSearchItem(searchItem), [searchItem])

  // Map view
  const mapViewCoords = useMemo(() => {
    if (searchItem?.searchType === 'bounds') {
      const coords = searchItem.value?.split(',')
      // This should always split to 4 coordinates and adding for safety
      if (coords && coords.length === 4) {
        const numCoords = coords.map(parseFloat)
        return getCenterCoordinates(numCoords as [number, number, number, number])
      }
    }
    return []
  }, [searchItem])
  const [place] = usePlaceByCoords(mapViewCoords[0], mapViewCoords[1])

  // Destination from query params, or map view
  const lePlaceId = placeId ?? getDestinationIdFromURLSearchParams(queryParams) ?? getLandmarkIdFromURLSearchParams(queryParams) ?? place?.id

  // Offer from query params
  const offerId = useMemo(() => {
    if (bedbankPropertyId || !searchItem) {
      return undefined
    }
    return getOfferIdFromSearchItem(searchItem)
  }, [bedbankPropertyId, searchItem])
  const [offerFromQueryParams] = useOffer(offerId)

  useEffect(() => {
    // Bedbank property searches lack place ids, so resolve coordinates before budget lookup.
    const hasLocationSource = !!offer || !!placeId || (lat != null && lng != null)
    if (!bedbankPropertyId || hasLocationSource) {
      setPropertyLocation(undefined)
      return
    }

    let isActive = true
    const propertyId = bedbankPropertyId
    setPropertyLocation(undefined)

    async function resolvePropertyLocation() {
      const location = await getBedbankPropertyLocation(propertyId, {
        regionCode: currentRegionCode,
        currency: currencyCode,
      }).catch(() => undefined)

      if (isActive) {
        setPropertyLocation(location)
      }
    }

    resolvePropertyLocation()

    return () => {
      isActive = false
    }
  }, [bedbankPropertyId, currencyCode, currentRegionCode, lat, lng, offer, placeId])

  // The separate map budget Redux store should only be set with the current
  // search result location budget, not for anything prefetched via placeId or offer.
  const setAsMapBudget = !placeId && !offer

  const {
    toBeCalculatedBusinessBudget,
    budgetKey,
  } = useMemo(() => {
    const toBeCalculatedBudget: App.BusinessTraveller.ToBeCalculatedBusinessBudget = {
      currencyCode,
      lePlaceId,
      // The lng and lat are specifically for address search
      // In the typeahead, it would pass in the lat and lng as part of the redux key to store the budget
      // In the search url, the search item generated from the url would have the lat and lng to generate the key and get the budget
      longitude:
        lng?.toString() ??
        (searchItem?.searchType === 'lat_long' ? searchItem.lng?.toString() : undefined) ??
        propertyLocation?.longitude.toString(),
      latitude:
        lat?.toString() ??
        (searchItem?.searchType === 'lat_long' ? searchItem.lat?.toString() : undefined) ??
        propertyLocation?.latitude.toString(),
    }

    // Prefer offer passed in
    const offerToUse = offer ?? offerFromQueryParams
    if (offerToUse) {
      const locationData = getLocationDataFromOffer(offerToUse)
      toBeCalculatedBudget.longitude = locationData.location.longitude
      toBeCalculatedBudget.latitude = locationData.location.latitude
    }

    return {
      toBeCalculatedBusinessBudget: toBeCalculatedBudget,
      budgetKey: generateBusinessBudgetKey(toBeCalculatedBudget),
    }
  }, [currencyCode, lePlaceId, offer, offerFromQueryParams, lat, lng, propertyLocation, searchItem])

  const budgetData = budgetKey ? businessBudgets[budgetKey] : undefined

  // Since all fields are optional this ensures request has either lePlaceId or long/lat
  const hasValidLocation = toBeCalculatedBusinessBudget.lePlaceId || (toBeCalculatedBusinessBudget.longitude && toBeCalculatedBusinessBudget.latitude)

  useEffect(() => {
    if (!enabled) {
      return
    }

    if (offerRequired && !offer && !offerFromQueryParams) {
      return
    }

    if (businessId && toBeCalculatedBusinessBudget && hasValidLocation) {
      appDispatch(fetchBusinessAccommodationPolicy(businessId))
      appDispatch(fetchBusinessBudget(businessId, [budgetKey, toBeCalculatedBusinessBudget], { setAsMapBudget }))
      appDispatch(fetchBusinessApprovalPolicy(businessId))
    }
  }, [appDispatch, budgetKey, businessId, enabled, hasValidLocation, offer, offerFromQueryParams, offerRequired, toBeCalculatedBusinessBudget, setAsMapBudget])

  return {
    budgetData,
    currentBudget: budgetData?.budgets?.[0],
    budgetKey,
    toBeCalculatedBusinessBudget,
  }
}

export default useBusinessTravellerBusinessBudgetFetcher
